import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Big } from 'big.js';
import type {
  ChartData,
  ChartDataset,
  CoreChartOptions,
  DatasetChartOptions,
  ElementChartOptions,
  LineControllerChartOptions,
  PluginChartOptions,
  ScaleChartOptions,
  ScriptableContext,
  TooltipItem,
  TooltipOptions,
} from 'chart.js';
import type { DeepPartial } from '@interxfi/utilities/types/partial';
import { getCSSVariableValue } from '../../../util';
import { getRainbowLinearGradient } from '../utils';
import { LineChart } from './line-chart';
import {
  buildStockPriceInspectionPlugin,
  getStockPriceInspectionIndex,
  STOCK_PRICE_DATASET_LABEL,
  STOCK_PRICE_MARKER_RADIUS,
} from './stock-price-inspection';
import { stockPriceLinePlugin } from './stock-price-line-plugin';

type LineChartOptions = DeepPartial<
  CoreChartOptions<'line'> &
    ElementChartOptions<'line'> &
    PluginChartOptions<'line'> &
    DatasetChartOptions<'line'> &
    ScaleChartOptions<'line'> &
    LineControllerChartOptions
>;

const defaultOptions = {
  responsive: true,
  scales: {
    x: {
      grid: {
        display: false,
      },
    },
    y: {
      grid: {
        display: false,
      },
    },
  },
} as const satisfies LineChartOptions;

const isPriceTooltipItem = (item: TooltipItem<'line'>) => item.dataset.label === STOCK_PRICE_DATASET_LABEL;

export type StockPriceLineChartData = Omit<ChartData<'line'>, 'datasets'> & {
  dataset: Omit<ChartDataset<'line'>, 'label'>;
};

function mapStockPriceDataToChartData(data: StockPriceLineChartData, withProjection?: boolean): ChartData<'line'> {
  const { data: priceData, ...rest } = data.dataset;
  const lastPriceIndex = priceData.findLastIndex((price) => {
    if (price === null) {
      return false;
    }

    try {
      Big(price.toString());
      return true;
    } catch {
      return false;
    }
  });
  const projectionLength = (data.labels?.length || 0) - (lastPriceIndex + 1);

  const projection =
    projectionLength > 0 && lastPriceIndex !== null
      ? new Array(data.labels?.length || 0).fill(null).map((_, i) => (i < lastPriceIndex ? null : priceData[lastPriceIndex]))
      : [];

  return {
    ...data,
    datasets: [
      {
        data: priceData,
        pointRadius: 0,
        spanGaps: true,
        pointStyle: false,
        pointBackgroundColor: 'transparent',
        borderWidth: 2,
        borderColor: ({ chart: { ctx, chartArea, scales } }: ScriptableContext<'line'>) =>
          chartArea ? getRainbowLinearGradient(ctx, chartArea.left, scales.x.getPixelForValue(lastPriceIndex)) : undefined,
        ...rest,
        label: STOCK_PRICE_DATASET_LABEL,
      },
      ...(withProjection
        ? [
            {
              label: 'Projection',
              data: projection,
              pointRadius: 0,
              spanGaps: false,
              pointStyle: false,
              pointBackgroundColor: 'transparent',
              borderWidth: 2,
              borderColor: getCSSVariableValue('--ixf-color-neutral-md') || '#e4e7e7',
            } as const,
          ]
        : []),
    ],
  };
}

interface StockPriceLineChartProps {
  data: StockPriceLineChartData;
  onInspect?: (dataIndex: number | null) => void;
  options?: LineChartOptions;
  withProjection?: boolean;
}

export function StockPriceLineChart({ data, onInspect, options, withProjection = true }: StockPriceLineChartProps) {
  const selectionRef = useRef<number | null>(null);
  const onInspectRef = useRef(onInspect);
  const inspectionEnabled = Boolean(onInspect);

  useEffect(() => {
    onInspectRef.current = onInspect;
  }, [onInspect]);

  const publishInspection = useCallback((next: number | null) => {
    if (selectionRef.current === next) {
      return;
    }

    selectionRef.current = next;
    onInspectRef.current?.(next);
  }, []);

  const [plugins] = useState(() => [stockPriceLinePlugin, buildStockPriceInspectionPlugin(() => selectionRef.current)]);

  const handleExternalTooltip = useCallback<TooltipOptions<'line'>['external']>(
    ({ tooltip }) => {
      publishInspection(getStockPriceInspectionIndex(tooltip));
    },
    [publishInspection],
  );

  // TODO: deep clone library
  const mergedOptions = useMemo(() => {
    const base = {
      ...defaultOptions,
      ...options,
    };

    if (!inspectionEnabled) {
      return base;
    }

    return {
      ...base,
      interaction: { axis: 'x', intersect: false, mode: 'index' } as const,
      layout: { padding: STOCK_PRICE_MARKER_RADIUS, ...base.layout },
      plugins: {
        ...base.plugins,
        tooltip: {
          enabled: false,
          external: handleExternalTooltip,
          filter: isPriceTooltipItem,
        },
      },
    };
  }, [handleExternalTooltip, inspectionEnabled, options]);

  const chartData = useMemo(() => mapStockPriceDataToChartData(data, withProjection), [data, withProjection]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: chartData identity is the reset trigger
  useEffect(() => {
    publishInspection(null);

    return () => {
      publishInspection(null);
    };
  }, [chartData, publishInspection]);

  return <LineChart data={chartData} options={mergedOptions} plugins={plugins} />;
}
